import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type TimestampUTC, type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import {
  AI_MODEL_CHOICE_OPTIONS,
  LANGUAGE_ENCODING_OPTIONS,
  LVLM_PERFORMANCE_MODES,
  LVLM_USAGE,
  PROCESS_BULK_OPTIONS,
} from './constants';
import { queryKeysInvoiceAiModel } from './queryKeys';
import { type AiModelSettings } from './useQueryInvoiceAiModelSettings';
import { zodAiModelEntrySchema } from './validations';

const zodCreateInvoiceAiModelSettingsPayloadSchema = z
  .object({
    aiModels: z.array(zodAiModelEntrySchema).min(1),
    diPlusAiFoundryFieldsToReplace: z.array(z.string()).optional().default([]),
    languageEncoding: z.enum(LANGUAGE_ENCODING_OPTIONS).nullable().optional(),
    lvlmPerformanceMode: z.enum(LVLM_PERFORMANCE_MODES).optional(),
    lvlmUsage: z.enum(LVLM_USAGE).optional(),
    modelChoice: z.enum(AI_MODEL_CHOICE_OPTIONS).nullable().optional(),
    processBulk: z.enum(PROCESS_BULK_OPTIONS).nullable().optional(),
  })
  .transform((data) => {
    if (data?.lvlmUsage === 'false') {
      // Remove LVLM performance mode if LVLM is not used
      data.lvlmPerformanceMode = undefined;
    }

    return withoutObjectKeysWhereValueIs(data, [null, undefined, '']);
  });

type CreateInvoiceAiModelSettingsPayload = z.infer<
  typeof zodCreateInvoiceAiModelSettingsPayloadSchema
>;

type CreateInvoiceAiModelSettingsResponse = {
  id: UUID;
  aiModelSettings: AiModelSettings;
  createdOn: TimestampUTC;
  modifiedOn: TimestampUTC | undefined;
};

/**
 * Creates invoice AI model settings.
 *
 * @param aiModelSettings - An object containing the AI model settings to be created.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/create_invoice_ai_model_settings_vadmin_invoice_ai_model_settings_post
 */
export const createInvoiceAiModelSettings = async (
  aiModelSettings: CreateInvoiceAiModelSettingsPayload,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.INVOICE_AI_MODEL.SETTINGS.CREATE(), {
        json: aiModelSettings,
        validate: { body: zodCreateInvoiceAiModelSettingsPayloadSchema },
      })
      .json<CreateInvoiceAiModelSettingsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error creating invoice AI model settings',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Custom hook for creating invoice AI model settings using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/create_invoice_ai_model_settings_vadmin_invoice_ai_model_settings_post
 */
export const useMutationCreateInvoiceAiModelSettings = (
  options?: UseMutationOptions<
    CreateInvoiceAiModelSettingsResponse,
    unknown,
    CreateInvoiceAiModelSettingsPayload
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (aiModelSettings: CreateInvoiceAiModelSettingsPayload) =>
      createInvoiceAiModelSettings(aiModelSettings),
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getAllSettings({}),
      });
    },
    ...options,
  });
};
